/**
 * Customer retail pricing for the "Plus" (with-insurance) plans.
 *
 * These are the prices SHOWN TO CUSTOMERS. They are the same numbers in EUR
 * (.io) and GBP (.co.uk) — only the currency symbol differs. They are NOT the
 * internal battleface premium (which must never be shown).
 *
 * Age bands: under 65, 65–74, 75–80. Over 80 = not eligible for insurance.
 */
export const PLUS_PRICES = {
  eur: {
    individualPlus: { under65: 49.95, band6574: 69.95, band7580: 79.95 },
    corporatePlus: { under65: 51.95, band6574: 71.95, band7580: 81.95 },
  },
  gbp: {
    individualPlus: { under65: 49.95, band6574: 69.95, band7580: 79.95 },
    corporatePlus: { under65: 51.95, band6574: 71.95, band7580: 81.95 },
  },
  dkk: {
    individualPlus: { under65: 399.95, band6574: 549.95, band7580: 649.95 },
    corporatePlus: { under65: 419.95, band6574: 569.95, band7580: 669.95 },
  },
  sek: {
    individualPlus: { under65: 599.95, band6574: 799.95, band7580: 949.95 },
    corporatePlus: { under65: 629.95, band6574: 829.95, band7580: 979.95 },
  },
  nok: {
    individualPlus: { under65: 599.95, band6574: 799.95, band7580: 949.95 },
    corporatePlus: { under65: 629.95, band6574: 829.95, band7580: 979.95 },
  },
  usd: {
    individualPlus: { under65: 59.95, band6574: 89.95, band7580: 99.95 },
    corporatePlus: { under65: 64.95, band6574: 94.95, band7580: 104.95 },
  },
};

// Currencies in which the Plus (insurance) plans are offered.
export const PLUS_CURRENCIES = Object.keys(PLUS_PRICES);

/**
 * Compute age (in whole years) from a date of birth.
 *
 * Accepts a Date, an ISO string, or the app's stored slash format. Stored
 * birthDates are day-first ("DD/MM/YYYY") — the same convention the app's own
 * parseBirthDate helper uses — with some legacy records in "MM/DD/YYYY". A raw
 * `new Date("17/02/1996")` throws an Invalid Date (month 17), which previously
 * made valid members look ineligible; this parser mirrors parseBirthDate so a
 * day-first value like 17/02/1996 is read correctly.
 */
export function getAgeFromDob(dob) {
  if (!dob) return null;
  let d;
  if (dob instanceof Date) {
    d = dob;
  } else if (typeof dob === "string" && dob.includes("/")) {
    const parts = dob.split("/").map((p) => Number(p));
    if (parts.length === 3 && parts.every((n) => !isNaN(n))) {
      const [a, b, y] = parts;
      if (a > 12) d = new Date(y, b - 1, a);       // DD/MM/YYYY
      else if (b > 12) d = new Date(y, a - 1, b);  // MM/DD/YYYY (legacy)
      else d = new Date(y, b - 1, a);              // ambiguous → DD/MM/YYYY
    } else {
      d = new Date(dob);
    }
  } else {
    d = new Date(dob);
  }
  if (!d || isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age;
}

/** Map an age to a band key, or "ineligible" for over-80 / "unknown" if age missing. */
export function getAgeBand(age) {
  if (age == null || isNaN(age)) return "unknown";
  if (age < 65) return "under65";
  if (age <= 74) return "band6574";
  if (age <= 80) return "band7580";
  return "ineligible";
}

/**
 * Retail price for a Plus plan given a date of birth.
 * planKey: "individualPlus" | "corporatePlus".
 * Returns a number, or null if the age is unknown or the member is ineligible.
 */
export function getPlusPrice(planKey, dob, currency = "eur") {
  const band = getAgeBand(getAgeFromDob(dob));
  if (band === "unknown" || band === "ineligible") return null;
  const cur = String(currency || "eur").toLowerCase();
  const byCurrency = PLUS_PRICES[cur] || PLUS_PRICES.eur;
  const table = byCurrency[planKey];
  return table ? table[band] ?? null : null;
}
